import { useId, useState } from 'react'
import { Link } from 'react-router'
import { hostError } from '../../api'
import { useSecondFactor } from '../SecondFactor'
import { routePath, type NodeImpact } from './api'
import { plural } from './format'

/*
 * Network actions the center confirms (04-admin-api.md §3.1; R0.9g, decision 48):
 * PATCH of a node (relay off, egress / hub off, egress via_center → direct) and
 * the revoke of a node with live legs.
 *
 * The flow is the server's. The console first shows its own inline confirmation
 * (what changes, what it touches as far as the console knows); the action then
 * goes out. A center that finds live legs on the routes touched refuses it with
 * 400 confirm_required, the word and `impact` — nothing happened yet, so that
 * first request works as the preview. The console then shows the center's
 * impact and a field for the word; the button stays off until it matches. The
 * request goes again with `confirm`; a 403 totp_required opens the code window
 * (SecondFactor) and repeats it with the code. Without live legs the center asks
 * for nothing and the action is done by the first request.
 *
 * A revoke never goes out without its word (the node's name): an older center
 * revokes on any POST, so the console sends the word it already asked for.
 */

/** The center asked for a word: what it said. */
export interface Asked {
  word: string
  impact?: NodeImpact
  /** the server's text */
  message: string
}

/** 400 confirm_required of a ConfirmError, or null. */
export function askedOf(e: unknown): Asked | null {
  const h = hostError(e)
  if (!h || h.code !== 'confirm_required') return null
  return { word: h.confirm ?? '', impact: h.impact, message: h.error }
}

export type ConfirmResult<T> = { ok: true; data: T } | { ok: false; asked: Asked }

/**
 * Runs a confirmed network action. `send` gets the word (when the center asked
 * for one, or the caller has it already) and the code (second try after 403).
 * Returns the answer, or what the center asked for; any other error is thrown
 * (FactorCancelled when the code window was closed).
 */
export function useConfirmedAction() {
  const factor = useSecondFactor()
  const [asked, setAsked] = useState<Asked | null>(null)
  const [word, setWord] = useState('')
  const run = async <T,>(send: (c: { confirm?: string; totp?: string }) => Promise<T>, preset?: string): Promise<ConfirmResult<T>> => {
    const confirm = asked ? word.trim() : preset
    try {
      const data = await factor((totp) => send({ confirm: confirm || undefined, totp }))
      setAsked(null)
      setWord('')
      return { ok: true, data }
    } catch (e) {
      const a = askedOf(e)
      if (!a) throw e
      // the first refusal opens the word field; a wrong word keeps it, emptied
      setAsked(a)
      setWord('')
      return { ok: false, asked: a }
    }
  }
  const reset = () => {
    setAsked(null)
    setWord('')
  }
  const matches = !!asked && word.trim() === asked.word
  return { asked, word, setWord, matches, run, reset }
}

const TAKES: Record<NodeImpact['takes'][number], string> = { relay: 'the relay', egress: 'the egress role', hub: 'the hub role' }

/** The center's impact in words: "Takes the egress role from fra-1: 2 live legs on 2 routes (2 streams)". */
export function ServerImpact({ impact, node, verb = 'Takes', testId }: { impact?: NodeImpact; node: string; verb?: string; testId: string }) {
  if (!impact) {
    return (
      <p className="text-[13px] text-text-soft" data-testid={testId}>
        The center says this breaks the air of streams through {node}.
      </p>
    )
  }
  const what = impact.takes?.length ? impact.takes.map((t) => TAKES[t]).join(', ') : 'everything'
  const ids = impact.route_ids ?? []
  return (
    <div className="text-[13px]" data-testid={testId} data-legs={impact.legs} data-routes={impact.routes} data-streams={impact.streams}>
      <p className="font-medium text-text">The center checked it</p>
      <p className="text-text-soft">
        {verb} {what} from {node}: <span className="tabular font-mono">{plural(impact.legs, 'live leg')}</span> on <span className="tabular font-mono">{plural(impact.routes, 'route')}</span> (
        <span className="tabular font-mono">{plural(impact.streams, 'stream')}</span>) lose their path now.
      </p>
      {ids.length > 0 && (
        <p className="mt-1 flex flex-wrap gap-x-2 gap-y-1 font-mono text-xs" data-testid={`${testId}-routes`}>
          {ids.slice(0, 12).map((r) => (
            <Link key={r} to={routePath(r)} className="text-text-soft underline underline-offset-2">
              {r}
            </Link>
          ))}
          {(ids.length > 12 || impact.routes > ids.length) && <span className="text-muted">+{Math.max(ids.length, impact.routes) - Math.min(ids.length, 12)} more</span>}
        </p>
      )}
    </div>
  )
}

/** "Type DISABLE to confirm": the field of the word the center asked for. */
export function WordField({ word, value, onChange, testId, label }: { word: string; value: string; onChange: (v: string) => void; testId: string; label?: string }) {
  const id = useId()
  return (
    <label className="flex flex-col gap-1 text-[13px]" htmlFor={id}>
      <span className="text-text-soft">
        {label ?? 'Type'} <b className="font-mono text-text">{word}</b> to confirm
      </span>
      <input
        id={id}
        data-testid={testId}
        data-word={word}
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        className="tabular min-h-9 max-w-[260px] rounded-md border border-line-2 bg-bg px-2.5 font-mono text-sm text-text focus:border-accent focus:shadow-focus focus:outline-none max-md:min-h-[44px]"
      />
    </label>
  )
}

/** Text of a refused network action that is not a step of the dialog. */
export function actionError(e: unknown, what: string): string {
  const h = hostError(e)
  if (h?.code === 'provisioner_unavailable') return `${what}: the center could not check your code (the provisioner did not answer). Nothing changed; try again in a minute.`
  if (h?.code === 'totp_not_enrolled') return `${what}: your account has no second factor yet. Nothing changed.`
  if (h?.code === 'totp_locked') return `${what}: too many wrong codes, confirmations are locked${h.retry_after_s ? ` for ${Math.ceil(h.retry_after_s / 60)} min` : ''}. Nothing changed.`
  if (h?.status === 409) return `${what}: the node is revoked, its settings can't change any more.`
  if (h) return `${what}: ${h.status} ${h.error}. Nothing changed.`
  return `${what}: ${(e as Error).message}`
}
